import { buildOnSiteSizePreviewCss } from '../onsite/sizing';

/**
 * On-Site live preview — fidelity-first.
 *
 * Renders the API campaign HTML as-is (template CSS + Advanced Styling).
 * Only adds:
 *  1) SDK-equivalent size metrics (no !important, so Advanced !important wins)
 *  2) Transparent page so the host BG simulates the website behind the message
 *
 * Host chrome (toolbar, BG picker, Desktop/Mobile iframe width) is not part of
 * the campaign — it only simulates viewport / page background.
 */

export const ONSITE_PREVIEW_DEFAULT_BG = '#c0c0c0';
export const ONSITE_PREVIEW_MOBILE_WIDTH_PX = 390;

function sanitizeBgColor(color) {
  if (typeof color !== 'string') return ONSITE_PREVIEW_DEFAULT_BG;
  const trimmed = color.trim();
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)) return trimmed;
  if (/^rgb(a)?\(/i.test(trimmed)) return trimmed;
  return ONSITE_PREVIEW_DEFAULT_BG;
}

export function prepareOnSitePreviewHtml(html, { configuration = {} } = {}) {
  if (!html || typeof html !== 'string') return html || '';
  const sizeCss = buildOnSiteSizePreviewCss(configuration);
  // Backend theme CSS often ships `height: 100%` on the card (and `height: 55%`
  // on the image) even when Advanced Styling is off — that stretches the modal
  // to the full iframe. Auto/Preset have no height metric; card must hug content.
  const tag = `<style data-onsite-preview-sdk>
${sizeCss}
html, body {
  background: transparent !important;
  background-color: transparent !important;
  height: auto !important;
  min-height: 100%;
  margin: 0;
}
.onsite-text-layout,
.onsite-image-layout,
.onsite-coupon-layout {
  height: auto !important;
  max-height: none !important;
}
.onsite-text-layout .onsite-img-container,
.onsite-text-layout img.onsite-img-container,
img.onsite-img-container {
  aspect-ratio: 560 / 250 !important;
  height: auto !important;
  max-height: none !important;
  flex-shrink: 0 !important;
}
.onsite-image-layout img,
img.onsite-image-layout {
  aspect-ratio: 560 / 320 !important;
  height: auto !important;
  max-height: none !important;
}
img.onsite-img-container,
.onsite-img-container img,
.onsite-image-layout img,
img.onsite-image-layout {
  object-fit: contain !important;
  object-position: center !important;
}
</style>`;
  let out = html
    .replace(/<style[^>]*data-onsite-live-img-fix[^>]*>[\s\S]*?<\/style>/i, '')
    .replace(/<style[^>]*data-onsite-preview-sdk[^>]*>[\s\S]*?<\/style>/i, '');
  if (/<\/head>/i.test(out)) {
    return out.replace(/<\/head>/i, `${tag}</head>`);
  }
  return `${tag}${out}`;
}

function buildHostDocument(
  contentUrl,
  { defaultMode = 'desktop', backgroundColor = ONSITE_PREVIEW_DEFAULT_BG } = {}
) {
  const mobileW = ONSITE_PREVIEW_MOBILE_WIDTH_PX;
  const bg = sanitizeBgColor(backgroundColor);
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>On-Site Preview</title>
  <style>
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      height: 100%;
      font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
      background: ${bg};
    }
    .toolbar {
      position: fixed;
      top: 0; left: 0; right: 0;
      z-index: 10000;
      height: 48px;
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 0 16px;
      background: #1f1f1f;
      color: #f5f5f5;
      font-size: 13px;
    }
    .toolbar strong { font-weight: 600; margin-right: 4px; }
    .toolbar .hint {
      opacity: 0.65;
      font-size: 12px;
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .toolbar button {
      appearance: none;
      border: 1px solid #555;
      background: #2a2a2a;
      color: #eee;
      border-radius: 6px;
      padding: 6px 12px;
      cursor: pointer;
      font-size: 13px;
    }
    .toolbar button.active {
      background: #2847e2;
      border-color: #2847e2;
      color: #fff;
    }
    .bg-control {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      margin-left: 4px;
      font-size: 12px;
      color: #ccc;
    }
    .bg-control input[type="color"] {
      width: 28px;
      height: 28px;
      padding: 0;
      border: 1px solid #555;
      border-radius: 4px;
      background: transparent;
      cursor: pointer;
    }
    .bg-control input[type="text"] {
      width: 78px;
      height: 28px;
      border: 1px solid #555;
      border-radius: 4px;
      background: #2a2a2a;
      color: #eee;
      padding: 0 6px;
      font-size: 12px;
      font-family: ui-monospace, monospace;
    }
    .stage {
      position: fixed;
      top: 48px; left: 0; right: 0; bottom: 0;
      display: flex;
      justify-content: center;
      align-items: stretch;
      background-color: ${bg};
    }
    .stage.desktop iframe {
      width: 100%;
      height: 100%;
      border: 0;
      background: transparent;
    }
    .stage.mobile {
      padding: 16px 0;
      align-items: center;
    }
    .stage.mobile iframe {
      width: ${mobileW}px;
      max-width: 100%;
      height: calc(100% - 32px);
      border: 0;
      border-radius: 12px;
      background: transparent;
      box-shadow: 0 8px 32px rgba(0,0,0,0.18);
    }
  </style>
</head>
<body>
  <div class="toolbar">
    <strong>On-Site Preview</strong>
    <button type="button" data-mode="desktop" class="${
      defaultMode === 'desktop' ? 'active' : ''
    }">Desktop</button>
    <button type="button" data-mode="mobile" class="${
      defaultMode === 'mobile' ? 'active' : ''
    }">Mobile (${mobileW}px)</button>
    <label class="bg-control" title="Simulates the website page behind the onsite message">
      <span>BG</span>
      <input type="color" id="bg-color" value="${bg}" />
      <input type="text" id="bg-hex" value="${bg}" maxlength="7" spellcheck="false" />
    </label>
    <span class="hint">Campaign HTML as saved (incl. Advanced Styling). Size = Auto/Preset/Custom. BG = fake website only.</span>
  </div>
  <div class="stage ${defaultMode}">
    <iframe id="onsite-live-frame" title="On-Site preview" src="${contentUrl}"></iframe>
  </div>
  <script>
    (function () {
      var stage = document.querySelector('.stage');
      var frame = document.getElementById('onsite-live-frame');
      var colorInput = document.getElementById('bg-color');
      var hexInput = document.getElementById('bg-hex');
      var buttons = document.querySelectorAll('.toolbar button[data-mode]');

      function normalizeHex(color) {
        var v = String(color || '').trim();
        if (v && v.charAt(0) !== '#') v = '#' + v;
        if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v)) return null;
        if (v.length === 4) {
          v = '#' + v[1] + v[1] + v[2] + v[2] + v[3] + v[3];
        }
        return v.toLowerCase();
      }

      function applyBg(color) {
        var hex = normalizeHex(color);
        if (!hex) return;
        // Only the host "website" page — campaign document stays transparent.
        document.body.style.background = hex;
        stage.style.backgroundColor = hex;
        frame.style.background = 'transparent';
        try {
          var doc = frame.contentDocument;
          if (doc) {
            if (doc.documentElement) {
              doc.documentElement.style.setProperty('background', 'transparent', 'important');
              doc.documentElement.style.setProperty('background-color', 'transparent', 'important');
            }
            if (doc.body) {
              doc.body.style.setProperty('background', 'transparent', 'important');
              doc.body.style.setProperty('background-color', 'transparent', 'important');
            }
          }
        } catch (e) {}
        colorInput.value = hex;
        hexInput.value = hex;
      }

      buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var mode = btn.getAttribute('data-mode');
          stage.classList.remove('desktop', 'mobile');
          stage.classList.add(mode);
          buttons.forEach(function (b) {
            b.classList.toggle('active', b === btn);
          });
        });
      });

      colorInput.addEventListener('input', function () {
        applyBg(colorInput.value);
      });
      colorInput.addEventListener('change', function () {
        applyBg(colorInput.value);
      });
      hexInput.addEventListener('input', function () {
        if (normalizeHex(hexInput.value)) applyBg(hexInput.value);
      });
      hexInput.addEventListener('change', function () {
        applyBg(hexInput.value);
      });
      frame.addEventListener('load', function () {
        applyBg(colorInput.value);
      });
    })();
  </script>
</body>
</html>`;
}

/**
 * Opens API preview HTML in a new tab with Desktop / Mobile viewport toggle.
 * @returns {Window|null}
 */
export function openOnSiteLivePreviewTab(
  apiHtml,
  {
    defaultMode = 'desktop',
    backgroundColor = ONSITE_PREVIEW_DEFAULT_BG,
    configuration = {},
  } = {}
) {
  if (typeof window === 'undefined' || !apiHtml) return null;

  const bg = sanitizeBgColor(backgroundColor);
  const prepared = prepareOnSitePreviewHtml(apiHtml, { configuration });
  const contentBlob = new Blob([prepared], { type: 'text/html' });
  const contentUrl = URL.createObjectURL(contentBlob);

  const hostHtml = buildHostDocument(contentUrl, {
    defaultMode,
    backgroundColor: bg,
  });
  const hostBlob = new Blob([hostHtml], { type: 'text/html' });
  const hostUrl = URL.createObjectURL(hostBlob);

  const win = window.open(hostUrl, '_blank');
  if (!win) {
    URL.revokeObjectURL(contentUrl);
    URL.revokeObjectURL(hostUrl);
    return null;
  }

  setTimeout(() => {
    URL.revokeObjectURL(hostUrl);
    URL.revokeObjectURL(contentUrl);
  }, 60_000);

  return win;
}
